import React from "react";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import SettingsSideMenu from "screens/Settings/MyProfile/components/SettingsSideMenu";
import settingsShellTokens, {
  PHONE_SM_QUERY,
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";
import { useSetPageHeader } from "global/components/PageHeader/PageHeaderContext";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";

const { colors, layout, pageTitle } = settingsShellTokens;
const { phone, tablet } = settingsShellResponsiveTokens;

interface SettingsLayoutProps {
  /** Label matched against SettingsSideMenu entries to highlight the active nav item. */
  activeTab: string;
  /** Page heading shown above the content (design: 22px / 800). */
  title?: React.ReactNode;
  /** Right-aligned header content next to the title (search, primary action…). */
  headerAction?: React.ReactNode;
  /** Vertical gap between stacked content cards (MUI spacing units; 2.5 = 20px). */
  contentSpacing?: number;
  /**
   * CSS `order` for the header row below `md`. Screens whose reference puts a
   * card *above* the search/action pair on small viewports (Teams' seat meter)
   * set this, and give their own children matching orders. Left unset the
   * header keeps its natural position, first.
   */
  headerOrder?: number;
  children: React.ReactNode;
}

/**
 * Shared shell for every Settings sub-screen.
 *
 * Desktop (`md`+) is unchanged: a full-height 248px nav column separated by a
 * hairline rule, plus a centred 1000px content column. The tablet reference
 * narrows that rail to 206px, tints the active row orange and drops the user
 * card to the column's foot; the phone reference drops the rail entirely — the
 * sections live on their own index screen (`SettingsIndex`) and a sub-screen is
 * a full-bleed detail whose name is carried by the app bar, next to a back
 * arrow. Geometry, colours and type come from `Settings.tokens.ts`.
 */
const SettingsLayout: React.FC<SettingsLayoutProps> = ({
  activeTab,
  title,
  headerAction,
  contentSpacing = 2.5,
  headerOrder,
  children,
}) => {
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery((theme: Theme) =>
    theme.breakpoints.between("sm", "md"),
  );

  /*
    The phone reference puts the section name — not "Settings" — in the app bar
    with a back arrow to the index; the tablet keeps "Settings" there because
    the section name is already the page's own <h1>.
  */
  useSetPageHeader(
    isPhone ? activeTab : isTablet ? "Settings" : undefined,
    isPhone
      ? () => history.push(viewpaths.settingsViewPath)
      : undefined,
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        justifyContent: "center",
        // One page canvas at every width — the small references draw a warmer
        // one, but Settings stays on the desktop tint so the app reads as one
        // surface however wide the viewport is.
        bgcolor: colors.canvas,
        // Nothing above this point establishes a full-height flex chain, so the
        // canvas would stop at the content and leave white below it. Fill the
        // viewport minus the sticky top nav instead.
        minHeight: `calc(100vh - ${layout.navStickyTop}px)`,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: layout.shellMaxWidth,
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          px: { xs: 0, md: `${layout.shellPaddingX}px` },
          boxSizing: "border-box",
        }}
      >
        <Box
          component="nav"
          aria-label="Settings navigation"
          sx={{
            display: { xs: "none", sm: "block" },
            width: { sm: tablet.navWidth, md: layout.navWidth },
            flexShrink: 0,
            boxSizing: "border-box",
            borderRight: {
              sm: `1px solid ${tablet.navBorder}`,
              md: `1px solid ${colors.navBorder}`,
            },
            bgcolor: { sm: tablet.navBg, md: "transparent" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: { sm: tablet.navPadding, md: layout.navPadding },
              position: "sticky",
              top: `${layout.navStickyTop}px`,
              height: { sm: `calc(100vh - ${layout.navStickyTop}px)`, md: "auto" },
              maxHeight: { md: `calc(100vh - ${layout.navStickyTop}px)` },
              overflowY: "auto",
              boxSizing: "border-box",
            }}
          >
            <SettingsSideMenu activeTab={activeTab} />
          </Box>
        </Box>

        {/*
          A `div`, not a `main`: `App` already wraps every protected route in
          `<main id="main-content">`, and Settings screens are not `hideNavs`,
          so a `main` here rendered a second, nested main landmark — invalid,
          and it left screen-reader users with two "main" entries (WCAG 1.3.1).
        */}
        <Box
          data-route-focus=""
          tabIndex={-1}
          sx={{ flex: 1, minWidth: 0, outline: "none" }}
        >
          <Box
            sx={{
              maxWidth: layout.contentMaxWidth,
              mx: "auto",
              p: {
                xs: phone.detailPadding.std,
                sm: tablet.contentPadding,
                md: layout.contentPadding,
              },
              [PHONE_SM_QUERY]: { padding: phone.detailPadding.sm },
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: {
                  xs: phone.contentGap.std,
                  sm: tablet.contentGap,
                  md: contentSpacing,
                },
                [PHONE_SM_QUERY]: { gap: phone.contentGap.sm },
              }}
            >
              {/*
                When a screen reorders its header (see `headerOrder`), the
                tablet still wants the page title first — only the search and
                action pair move below the screen's own lead card. The title is
                lifted out of the ordered row for that one breakpoint; exactly
                one copy is ever rendered, so there is still a single <h1>.
              */}
              {headerOrder != null && typeof title === "string" && (
                <Typography
                  component="h1"
                  sx={{
                    display: { xs: "none", sm: "block", md: "none" },
                    order: 0,
                    color: colors.pageTitle,
                    fontSize: tablet.pageTitleFontSize,
                    fontWeight: pageTitle.fontWeight,
                    letterSpacing: pageTitle.letterSpacing,
                    lineHeight: pageTitle.lineHeight,
                  }}
                >
                  {title}
                </Typography>
              )}
              {(title || headerAction) && (
                <Box
                  sx={{
                    // The phone reference carries the screen name in the app
                    // bar instead, so the page's own header is dropped there
                    // unless the screen also renders actions beside it.
                    display: {
                      xs: headerAction ? "flex" : "none",
                      sm: "flex",
                    },
                    // Both small references run a screen's actions full width
                    // under the title rather than beside it.
                    order: { xs: headerOrder ?? 0, md: 0 },
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "center" },
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: layout.headerGap,
                  }}
                >
                  {typeof title === "string" ? (
                    <Typography
                      component="h1"
                      sx={{
                        display: {
                          xs: "none",
                          sm: headerOrder != null ? "none" : "block",
                          md: "block",
                        },
                        color: colors.pageTitle,
                        fontSize: {
                          sm: tablet.pageTitleFontSize,
                          md: pageTitle.fontSize,
                        },
                        fontWeight: pageTitle.fontWeight,
                        letterSpacing: pageTitle.letterSpacing,
                        lineHeight: pageTitle.lineHeight,
                      }}
                    >
                      {title}
                    </Typography>
                  ) : (
                    // Screens that supply their own heading node get the same
                    // treatment as a plain string title: the app bar carries
                    // the screen name on the phone, so the node is dropped
                    // there rather than repeating it.
                    <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0 }}>
                      {title}
                    </Box>
                  )}
                  {headerAction && (
                    <Box
                      sx={{
                        display: "flex",
                        width: { xs: "100%", md: "auto" },
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "stretch", md: "center" },
                        gap: layout.headerGap,
                        flexShrink: 0,
                      }}
                    >
                      {headerAction}
                    </Box>
                  )}
                </Box>
              )}
              {children}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsLayout;
